import { CheckboxSelectList } from "@/features/credentials/components/checkbox-select-list";
import { CredentialTypeSelector } from "@/features/credentials/components/credential-type-selector";
import { LookupMultiSelect } from "@/features/credentials/components/lookup-multi-select";
import { SeasonTeamTree } from "@/features/credentials/components/season-team-tree";
import type { Credential, CredentialType } from "@/features/credentials/models/credential";
import { useCompetitionsListQuery } from "@/features/credentials/queries/use-competitions-list";
import { useCreateCredentialMutation } from "@/features/credentials/queries/use-create-credential";
import { useUpdateCredentialMutation } from "@/features/credentials/queries/use-update-credential";
import { useVenuesListQuery } from "@/features/credentials/queries/use-venues-list";
import {
  createCredentialDefaultValues,
  getCreateCredentialSchema,
  getFormValuesFromCredential,
  getUpdateCredentialSchema,
  toCreateCredentialPayload,
  toUpdateCredentialPayload,
  type CreateCredentialFormValues,
} from "@/features/credentials/schemas/create-credential.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@cmde/shadcn";
import { EyeIcon, EyeOffIcon, Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export function CreateCredentialForm({
  mode = "create",
  sport,
  organizationId,
  credential,
  onCancel,
  onSuccess,
}: {
  mode?: "create" | "edit";
  sport: string;
  organizationId: string;
  credential?: Credential;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const createCredentialMutation = useCreateCredentialMutation();
  const updateCredentialMutation = useUpdateCredentialMutation();
  const isEdit = mode === "edit";
  const schema = useMemo(
    () => (isEdit ? getUpdateCredentialSchema(t) : getCreateCredentialSchema(t)),
    [isEdit, t],
  );

  const form = useForm<CreateCredentialFormValues>({
    resolver: zodResolver(schema),
    defaultValues: credential ? getFormValuesFromCredential(credential) : createCredentialDefaultValues,
  });

  useEffect(() => {
    if (credential) {
      form.reset(getFormValuesFromCredential(credential));
    }
  }, [credential, form]);

  const credentialType = form.watch("credentialType");
  const competitions = form.watch("competitions");
  const needsScopeLookups = credentialType !== "ALL";
  const isTeamUser = credentialType === "ENTITY";
  const isVenueUser = credentialType === "VENUE";

  const competitionsQuery = useCompetitionsListQuery({
    sport,
    organizationId,
    enabled: needsScopeLookups,
  });
  const venuesQuery = useVenuesListQuery({
    sport,
    organizationId,
    enabled: isVenueUser,
  });

  const competitionOptions = useMemo(
    () =>
      (competitionsQuery.data ?? []).map((competition) => ({
        id: String(competition.competitionId),
        name: competition.name,
      })),
    [competitionsQuery.data],
  );
  const venueOptions = useMemo(
    () =>
      (venuesQuery.data ?? []).map((venue) => ({
        id: String(venue.venueId),
        name: venue.name,
      })),
    [venuesQuery.data],
  );

  const handleTypeChange = (nextType: CredentialType) => {
    if (isEdit) {
      return;
    }

    form.setValue("credentialType", nextType);
    form.setValue("competitions", []);
    form.setValue("seasons", []);
    form.setValue("ids", []);
    form.clearErrors(["competitions", "seasons", "ids"]);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (isEdit) {
        if (!credential) {
          return;
        }

        await updateCredentialMutation.mutateAsync({
          sport,
          organizationId,
          credentialId: String(credential.credentialId),
          payload: toUpdateCredentialPayload(values, credential),
        });
        toast.success(t("credentials.edit.success"));
      } else {
        await createCredentialMutation.mutateAsync({
          sport,
          organizationId,
          payload: toCreateCredentialPayload(values),
        });
        toast.success(t("credentials.create.success"));
      }
      onSuccess();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(isEdit ? "credentials.edit.errors.submit_failed" : "credentials.create.errors.submit_failed");
      toast.error(message);
      if (!isEdit && message.toLowerCase().includes("username already")) {
        form.setError("username", { message });
      }
    }
  });

  const errors = form.formState.errors;
  const isSubmitting =
    form.formState.isSubmitting || createCredentialMutation.isPending || updateCredentialMutation.isPending;

  return (
    <form onSubmit={onSubmit} className="flex h-full flex-col gap-6">
      <FieldGroup>
        <Field>
          <FieldLabel>{t("credentials.create.fields.user_type")}</FieldLabel>
          <CredentialTypeSelector value={credentialType} onChange={handleTypeChange} disabled={isEdit} />
        </Field>

        <Field data-invalid={errors.name ? true : undefined}>
          <FieldLabel htmlFor="credential-name">{t("credentials.create.fields.name")}</FieldLabel>
          <Input id="credential-name" autoComplete="off" {...form.register("name")} />
          <FieldError errors={[errors.name]} />
        </Field>

        <Field data-invalid={errors.username ? true : undefined}>
          <FieldLabel htmlFor="credential-username">{t("credentials.create.fields.username")}</FieldLabel>
          <Input
            id="credential-username"
            autoComplete="off"
            disabled={isEdit}
            {...form.register("username")}
          />
          <FieldError errors={[errors.username]} />
        </Field>

        <Field data-invalid={errors.password ? true : undefined}>
          <FieldLabel htmlFor="credential-password">{t("credentials.create.fields.password")}</FieldLabel>
          <InputGroup>
            <InputGroupInput
              id="credential-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              {...form.register("password")}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                size="icon-xs"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={
                  showPassword
                    ? t("credentials.create.fields.hide_password")
                    : t("credentials.create.fields.show_password")
                }
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          {isEdit ? <FieldDescription>{t("credentials.edit.password_help")}</FieldDescription> : null}
          <FieldError errors={[errors.password]} />
        </Field>

        <Field data-invalid={errors.expiryDate ? true : undefined}>
          <FieldLabel htmlFor="credential-expiry-date">{t("credentials.create.fields.expiry_date")}</FieldLabel>
          <Input id="credential-expiry-date" type="date" {...form.register("expiryDate")} />
          <FieldError errors={[errors.expiryDate]} />
        </Field>

        {needsScopeLookups ? (
          <Field data-invalid={errors.competitions ? true : undefined}>
            <FieldLabel>{t("credentials.create.fields.competitions")}</FieldLabel>
            <Controller
              control={form.control}
              name="competitions"
              render={({ field }) => (
                <LookupMultiSelect
                  options={competitionOptions}
                  value={field.value}
                  onChange={(nextValue) => {
                    field.onChange(nextValue);
                    form.setValue("seasons", []);
                    if (isTeamUser) {
                      form.setValue("ids", []);
                    }
                  }}
                  isLoading={competitionsQuery.isLoading}
                  placeholder={t("credentials.create.fields.competitions_placeholder")}
                  searchPlaceholder={t("credentials.create.fields.search_placeholder")}
                  emptyString={t("credentials.create.fields.no_options")}
                />
              )}
            />
            <FieldError errors={[errors.competitions]} />
          </Field>
        ) : null}

        {isTeamUser ? (
          <Field data-invalid={errors.ids ? true : undefined}>
            <FieldLabel>{t("credentials.create.fields.teams")}</FieldLabel>
            <Controller
              control={form.control}
              name="ids"
              render={({ field }) => (
                <SeasonTeamTree
                  sport={sport}
                  organizationId={organizationId}
                  competitionIds={competitions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={competitions.length === 0}
                />
              )}
            />
            <FieldDescription>{t("credentials.create.fields.teams_help")}</FieldDescription>
            <FieldError errors={[errors.ids]} />
          </Field>
        ) : null}

        {isVenueUser ? (
          <Field data-invalid={errors.ids ? true : undefined}>
            <FieldLabel>{t("credentials.create.fields.venues")}</FieldLabel>
            <Controller
              control={form.control}
              name="ids"
              render={({ field }) => (
                <CheckboxSelectList
                  items={venueOptions}
                  value={field.value}
                  onChange={field.onChange}
                  isLoading={venuesQuery.isLoading}
                  searchPlaceholder={t("credentials.create.fields.search_venues")}
                  emptyLabel={t("credentials.create.fields.no_options")}
                  loadingLabel={t("credentials.create.fields.loading_venues")}
                  selectAllLabel={t("credentials.create.fields.select_all_venues")}
                  unnamedLabel={t("credentials.create.fields.unnamed_venue")}
                  otherSelectedLabel={t("credentials.create.fields.other_selected_venues")}
                />
              )}
            />
            <FieldDescription>{t("credentials.create.fields.venues_help")}</FieldDescription>
            <FieldError errors={[errors.ids]} />
          </Field>
        ) : null}
      </FieldGroup>

      <div className="mt-auto flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t(isEdit ? "credentials.edit.cancel" : "credentials.create.cancel")}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
          {t(isEdit ? "credentials.edit.submit" : "credentials.create.submit")}
        </Button>
      </div>
    </form>
  );
}
